import React, { useState, useEffect } from 'react';
import { useCart } from '../context/CartContext';
import { TiArrowBack } from "react-icons/ti";
import './ProductDetailPage.css';

const ProductDetailPage = ({ product, initialOptions, onBack, onMakeAnother, onSelectStore, autoAdd, onCheckout }) => {
  const { addToCart } = useCart();
  const [selectedSize, setSelectedSize] = useState(initialOptions?.size || 'small');
  const [selectedSweetness, setSelectedSweetness] = useState(initialOptions?.sweetness || 'normal');
  const [isTakeaway, setIsTakeaway] = useState(initialOptions?.takeaway || false);
  const [quantity, setQuantity] = useState(initialOptions?.quantity || 1);
  const [totalPrice, setTotalPrice] = useState(product.price);

  // Calcular precio base según tamaño
  const getBasePrice = () => {
    let basePrice = product.price;

    if (selectedSize === 'big') {
      if (product.category === 'coffee' || product.category === 'smoothies') {
        basePrice = product.price * 1.5;
      } else if (product.category === 'bakery') {
        basePrice = product.price * 1.5;
      }
    }

    return basePrice;
  };

  // Calcular recargo por dulzura
  const getSweetnessSurcharge = () => {
    if (selectedSweetness === 'more') {
      return 1.00;
    }
    return 0;
  };

  // Calcular precio total
  const calculateTotal = () => {
    const basePrice = getBasePrice();
    const sweetnessSurcharge = getSweetnessSurcharge();
    return (basePrice + sweetnessSurcharge) * quantity;
  };

  // Actualizar precio cuando cambien las opciones
  useEffect(() => {
    setTotalPrice(calculateTotal());
  }, [selectedSize, selectedSweetness, quantity]);

  // Auto-add effect
  useEffect(() => {
    if (autoAdd) {
      handleAddToCart(true);
    }
  }, [autoAdd]);

  const handleTakeawayChange = (value) => {
    setIsTakeaway(value);

    // Si selecciona "Yes" y tenemos la función onSelectStore, navegar a tiendas
    if (value === true && onSelectStore) {
      onSelectStore();
    }
  };

  const handleAddToCart = (skipAlert = false) => {
    const cartItem = {
      id: product.id,
      name: product.name,
      backgroundImage: product.backgroundImage,
      description: product.description,
      price: product.price,
      finalPrice: totalPrice / quantity,
      size: selectedSize,
      sweetness: selectedSweetness,
      takeaway: isTakeaway,
      quantity: quantity,
      total: totalPrice,
      category: product.category,
      store: isTakeaway ? (initialOptions?.selectedStore || 'pending') : null
    };

    addToCart(cartItem);

    if (skipAlert && onCheckout) {
      onCheckout();
      return;
    }

    // Mostrar confirmación
    alert(`${quantity}x ${product.name} added to cart!`);

    // Si es takeaway, ya se manejó la navegación en handleTakeawayChange
    if (!isTakeaway) {
      if (onMakeAnother) {
        onMakeAnother();
      } else {
        onBack();
      }
    }
  };

  const handleQuantityChange = (newQuantity) => {
    if (newQuantity >= 1 && newQuantity <= 10) {
      setQuantity(newQuantity);
    }
  };

  const sizeOptions = [
    { id: 'small', label: 'Small', description: 'Standard size' },
    { id: 'big', label: 'Big', description: '+50% size' }
  ];

  const sweetnessOptions = [
    { id: 'normal', label: 'Normal', description: 'Dulzura Normal ' },
    { id: 'less', label: 'Less', description: 'Dulzura reducida' },
    { id: 'more', label: 'More', description: 'Dulzura Extra (+$1.00)' }
  ];

  const takeawayOptions = [
    { id: 'no', label: 'No', description: 'Aqui' },
    { id: 'yes', label: 'Yes', description: 'Para llevar - Elige tienda' }
  ];

  return (
    <div className="product-detail-page">
      <div className="product-detail-container">
        {/* Header con botón de volver */}
        <div className="detail-header">
          <button className="back-button" onClick={onBack}>
            <TiArrowBack />
          </button>
          <h1>Personaliza tu pedido</h1>
        </div>

        {/* Información del producto */}
        <div className="product-info-section" style={{ backgroundImage: `url(${product.image})` }}>
          <div className="product-header">
            <h2 className="product-name">{product.name}</h2>
            <p className="product-description">{product.description}</p>
            <div className="base-price">
              Precio base: <span>${product.price.toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* Opciones de personalización */}
        <div className="customization-options">

          {/* Tamaño */}
          <div className="option-section">
            <h3>Tamaño</h3>
            <div className="option-buttons">
              {sizeOptions.map(option => (
                <button
                  key={option.id}
                  className={`option-btn ${selectedSize === option.id ? 'active' : ''}`}
                  onClick={() => setSelectedSize(option.id)}
                >
                  <span className="option-label">{option.label}</span>
                  <span className="option-description">{option.description}</span>
                  {selectedSize === option.id && option.id === 'big' && (
                    <span className="price-note">+50%</span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Dulzura (solo para bebidas) */}
          {(product.category === 'coffee' || product.category === 'smoothies') && (
            <div className="option-section">
              <h3>Dulzura</h3>
              <div className="option-buttons">
                {sweetnessOptions.map(option => (
                  <button
                    key={option.id}
                    className={`option-btn ${selectedSweetness === option.id ? 'active' : ''}`}
                    onClick={() => setSelectedSweetness(option.id)}
                  >
                    <span className="option-label">{option.label}</span>
                    <span className="option-description">{option.description}</span>
                    {selectedSweetness === option.id && option.id === 'more' && (
                      <span className="price-note">+$1.00</span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Takeaway - MODIFICADO */}
          <div className="option-section">
            <h3>Takeaway</h3>
            <div className="option-buttons">
              {takeawayOptions.map(option => (
                <button
                  key={option.id}
                  className={`option-btn ${isTakeaway === (option.id === 'yes') ? 'active' : ''}`}
                  onClick={() => handleTakeawayChange(option.id === 'yes')}
                >
                  <span className="option-label">{option.label}</span>
                  <span className="option-description">{option.description}</span>
                  {isTakeaway === (option.id === 'yes') && option.id === 'yes' && (
                    <span className="store-note">📍 Elige Tienda</span>
                  )}
                </button>
              ))}
            </div>

            {/* Mensaje informativo para takeaway */}
            {isTakeaway && (
              <div className="takeaway-info">
                <p>📦 Necesita seleccionar una tienda antes de Pagar</p>
              </div>
            )}
          </div>

          {/* Cantidad */}
          <div className="option-section">
            <h3>Cantidad</h3>
            <div className="quantity-selector">
              <button
                className="quantity-btn"
                onClick={() => handleQuantityChange(quantity - 1)}
                disabled={quantity <= 1}
              >
                −
              </button>
              <span className="quantity-display">{quantity}</span>
              <button
                className="quantity-btn"
                onClick={() => handleQuantityChange(quantity + 1)}
                disabled={quantity >= 10}
              >
                +
              </button>
            </div>
          </div>
        </div>

        {/* Resumen de precio */}
        <div className="price-summary">
          <h3>Detalle de precios</h3>
          <div className="price-details">
            <div className="price-row">
              <span>Base ({selectedSize}):</span>
              <span>${getBasePrice().toFixed(2)}</span>
            </div>

            {selectedSweetness === 'more' && (
              <div className="price-row">
                <span>Dulzura extra:</span>
                <span>+${getSweetnessSurcharge().toFixed(2)}</span>
              </div>
            )}

            <div className="price-row">
              <span>Cantidad:</span>
              <span>x{quantity}</span>
            </div>

            <div className="price-row total">
              <span>Total:</span>
              <span className="total-price">${totalPrice.toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* Botones de acción */}
        <div className="action-buttons">
          <button
            className="action-btn secondary"
            onClick={onMakeAnother}
            disabled={isTakeaway}
          >
            Elige otro Producto
          </button>

          <button
            className="action-btn primary"
            onClick={() => handleAddToCart(false)}
            disabled={isTakeaway}
          >
            {isTakeaway ? 'Select Store First' : `Add to Cart - $${totalPrice.toFixed(2)}`}
          </button>
        </div>

        {/* Nota informativa */}
        <div className="info-note">
          <p>💡 <strong>Note:</strong> El tamaño grande añade un 50% al precio base. El extra dulce tiene un costo de $1.00 por unidad.</p>
          {isTakeaway && (
            <p>📍 <strong>Takeaway:</strong> Seleccione una tienda para recoger antes de agregar al carrito.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductDetailPage;